
/* eslint-disable @typescript-eslint/strict-boolean-expressions -- Simplified boolean checks for UI state */
/**
 * Minimal PubSub + Helia Test Page
 *
 * Simple UI for testing libp2p pubsub and Helia file sharing:
 * - Connect to HAP via WebSocket
 * - Subscribe to test-pubsub topic
 * - Send and receive test messages
 * - Upload files to Helia and announce CID
 * - Display message counts
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';

import {
  MinimalLibp2pService,
  type MinimalLibp2p
} from '@/services/poc/libp2p-minimal.service';
import {
  MinimalHeliaService,
  type MinimalHeliaNode
} from '@/services/poc/helia-minimal.service';

const TEST_TOPIC = 'test-pubsub';
const MAX_LOG_ENTRIES = 100;
const STATUS_UPDATE_INTERVAL = 2000;
const MESSAGE_INTERVAL_MS = 16000; // 100 messages per minute = 1 every 600ms
const PEER_ID_DISPLAY_LENGTH = 20;
const PEER_ID_SHORT_LENGTH = 16;

interface LogEntry {
  timestamp: Date;
  type: 'info' | 'sent' | 'received' | 'error' | 'file';
  message: string;
}

interface FileClonedMessage {
  type: 'file-cloned';
  cid: string;
  name: string;
  size: number;
  originalUploader: string;
  clonedBy: string;
}

interface ClonedFile {
  cid: string;
  name: string;
  size: number;
  status: 'available' | 'cloning' | 'ready';
}

export default function PubsubTest(): React.ReactElement {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [peerId, setPeerId] = useState<string>('');
  const [connectionCount, setConnectionCount] = useState(0);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [receivedCount, setReceivedCount] = useState(0);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [messageInput, setMessageInput] = useState('Hello from browser!');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; cid: string; size: number; confirmed?: boolean }>>([]);
  const [clonedFiles, setClonedFiles] = useState<ClonedFile[]>([]);

  const nodeRef = useRef<MinimalLibp2p | null>(null);
  const serviceRef = useRef<MinimalLibp2pService | null>(null);
  const heliaNodeRef = useRef<MinimalHeliaNode | null>(null);
  const heliaServiceRef = useRef<MinimalHeliaService | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const statusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoSendIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messageSeqRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const addLog = useCallback((type: LogEntry['type'], message: string) => {
    setLogs((prev) => [...prev.slice(-(MAX_LOG_ENTRIES - 1)), { timestamp: new Date(), type, message }]);
  }, []);

  const updateStatus = useCallback(() => {
    if (nodeRef.current && serviceRef.current) {
      const info = serviceRef.current.getConnectionInfo(nodeRef.current);
      setConnectionCount(info.connections);
      setSubscriberCount(info.subscribers);

      const counts = serviceRef.current.getMessageCounts();
      setSentCount(counts.sent);
      setReceivedCount(counts.received);
    }
  }, []);

  const handleConnect = useCallback(async() => {
    if (isConnected || isConnecting) return;

    setIsConnecting(true);
    addLog('info', 'Connecting to P2P network...');

    try {
      // Create libp2p node
      const service = new MinimalLibp2pService();
      serviceRef.current = service;

      const node = await service.createNode();
      nodeRef.current = node;

      setPeerId(node.peerId.toString());
      addLog('info', `Connected with peer ID: ${node.peerId.toString().substring(0, PEER_ID_DISPLAY_LENGTH)}...`);

      // Create Helia node attached to libp2p
      addLog('info', 'Starting Helia...');
      const heliaService = new MinimalHeliaService();
      heliaServiceRef.current = heliaService;

      const heliaNode = await heliaService.createNode(node);
      heliaNodeRef.current = heliaNode;
      addLog('info', 'Helia ready for file uploads');

      // Subscribe to test topic
      addLog('info', `Subscribing to topic: ${TEST_TOPIC}`);
      const myPeerIdShort = node.peerId.toString().substring(0, PEER_ID_DISPLAY_LENGTH);
      unsubscribeRef.current = service.subscribeToTopic(node, TEST_TOPIC, (data, from) => {
        try {
          const text = new TextDecoder().decode(data);
          const parsed = JSON.parse(text) as Record<string, unknown>;

          // Handle file-cloned messages
          if (parsed.type === 'file-cloned') {
            const msg = parsed as unknown as FileClonedMessage;
            if (msg.originalUploader === myPeerIdShort) {
              // We uploaded this file - show confirmation
              addLog('file', `HAP confirmed clone: ${msg.name} (${msg.size} bytes)`);
              // Update uploaded file status if needed
              setUploadedFiles(prev => prev.map(f =>
                f.cid === msg.cid ? { ...f, confirmed: true } : f
              ));
            } else {
              // Another peer uploaded - auto-clone if we don't have it
              addLog('file', `New file available: ${msg.name} - auto-cloning...`);
              setClonedFiles(prev => {
                // Don't add duplicates
                if (prev.some(f => f.cid === msg.cid)) return prev;
                return [...prev, { cid: msg.cid, name: msg.name, size: msg.size, status: 'cloning' }];
              });

              // Clone via bitswap
              if (heliaServiceRef.current && heliaNodeRef.current) {
                void heliaServiceRef.current.getFile(heliaNodeRef.current, msg.cid)
                  .then(() => {
                    addLog('file', `Cloned: ${msg.name}`);
                    setClonedFiles(prev => prev.map(f =>
                      f.cid === msg.cid ? { ...f, status: 'ready' } : f
                    ));
                  })
                  .catch((err: Error) => {
                    addLog('error', `Clone failed for ${msg.name}: ${err.message}`);
                    setClonedFiles(prev => prev.filter(f => f.cid !== msg.cid));
                  });
              }
            }
            return;
          }

          addLog('received', `From ${from.substring(0, PEER_ID_SHORT_LENGTH)}...: ${JSON.stringify(parsed)}`);
        } catch {
          addLog('received', `From ${from.substring(0, PEER_ID_SHORT_LENGTH)}...: [binary ${data.length} bytes]`);
        }
      });

      // Start status updates
      statusIntervalRef.current = setInterval(updateStatus, STATUS_UPDATE_INTERVAL);

      // Start automated message sending (100 per minute)
      const peerIdShort = node.peerId.toString().substring(0, PEER_ID_DISPLAY_LENGTH);
      // autoSendIntervalRef.current = setInterval(async () => {
      //   if (nodeRef.current && serviceRef.current) {
      //     messageSeqRef.current++;
      //     const message = {
      //       type: 'test',
      //       source: 'browser',
      //       seq: messageSeqRef.current,
      //       peerId: peerIdShort,
      //       timestamp: Date.now()
      //     };
      //     try {
      //       await serviceRef.current.publishToTopic(nodeRef.current, TEST_TOPIC, JSON.stringify(message));
      //     } catch {
      //       // Silent fail for auto-send
      //     }
      //   }
      // }, MESSAGE_INTERVAL_MS);

      setIsConnected(true);
      addLog('info', 'Auto-sending 100 messages/minute. Ready to receive and upload files.');
    } catch (error) {
      addLog('error', `Connection failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsConnecting(false);
    }
  }, [isConnected, isConnecting, addLog, updateStatus]);

  const handleDisconnect = useCallback(async() => {
    if (!isConnected) return;

    addLog('info', 'Disconnecting...');

    if (autoSendIntervalRef.current !== null) {
      clearInterval(autoSendIntervalRef.current);
      autoSendIntervalRef.current = null;
    }

    if (statusIntervalRef.current !== null) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }

    if (unsubscribeRef.current !== null) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Stop Helia first
    if (heliaNodeRef.current !== null && heliaServiceRef.current !== null) {
      await heliaServiceRef.current.stopNode(heliaNodeRef.current);
      heliaNodeRef.current = null;
      heliaServiceRef.current = null;
    }

    // Then stop libp2p
    if (nodeRef.current !== null && serviceRef.current !== null) {
      await serviceRef.current.stopNode(nodeRef.current);
      nodeRef.current = null;
      serviceRef.current = null;
    }

    setIsConnected(false);
    setPeerId('');
    setConnectionCount(0);
    setSubscriberCount(0);
    setUploadedFiles([]);
    setClonedFiles([]);
    addLog('info', 'Disconnected');
  }, [isConnected, addLog]);

  const handleDownload = useCallback(async(cid: string, name: string) => {
    if (!heliaNodeRef.current || !heliaServiceRef.current) {
      addLog('error', 'Not connected - cannot download');
      return;
    }

    addLog('file', `Downloading: ${name}...`);
try {
      // 1. Get the Blob directly (Note: getFile now returns Promise<Blob>)
      const blob = await heliaServiceRef.current.getFile(heliaNodeRef.current, cid);

      // 2. Create URL directly from the result
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      addLog('file', `Downloaded: ${name}`);
    } catch (error) {
       addLog('error', `Download failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [addLog]);

  const handleSendMessage = useCallback(async() => {
    if (!isConnected || nodeRef.current === null || serviceRef.current === null) return;

    const message = {
      type: 'test',
      source: 'browser',
      text: messageInput,
      peerId: peerId.substring(0, PEER_ID_DISPLAY_LENGTH),
      timestamp: Date.now()
    };

    try {
      await serviceRef.current.publishToTopic(
        nodeRef.current,
        TEST_TOPIC,
        JSON.stringify(message)
      );
      addLog('sent', `Published: ${JSON.stringify(message)}`);
      updateStatus();
    } catch (error) {
      addLog('error', `Send failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [isConnected, messageInput, peerId, addLog, updateStatus]);

  const handleFileUpload = useCallback(async(event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!isConnected || !heliaNodeRef.current || !heliaServiceRef.current || !nodeRef.current || !serviceRef.current) {
      addLog('error', 'Not connected - cannot upload file');
      return;
    }

    const file = files[0];
    setIsUploading(true);
    addLog('file', `Uploading file: ${file.name} (${file.size} bytes)`);

    try {
      // Upload to Helia
      const result = await heliaServiceRef.current.addFile(heliaNodeRef.current, file);
      addLog('file', `File added to Helia with CID: ${result.cid}`);

      // Track uploaded files - pending until HAP confirms via file-cloned pubsub message
      setUploadedFiles(prev => [...prev, { name: result.name, cid: result.cid, size: result.size, confirmed: false }]);

      // Announce via pubsub
      const announcement = {
        type: 'file-announce',
        cid: result.cid,
        name: result.name,
        size: result.size,
        from: peerId.substring(0, PEER_ID_DISPLAY_LENGTH),
        timestamp: Date.now()
      };

      addLog('info', `Announcing file via pubsub: ${result.name}`);
      await serviceRef.current.publishToTopic(nodeRef.current, TEST_TOPIC, JSON.stringify(announcement));
      addLog('file', `File announced via pubsub, waiting for HAP confirmation...`);

    } catch (error) {
      addLog('error', `Upload failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isConnected, peerId, addLog]);

  // Cleanup on unmount
  useEffect(() => () => {
      if (autoSendIntervalRef.current !== null) {
        clearInterval(autoSendIntervalRef.current);
      }
      if (statusIntervalRef.current !== null) {
        clearInterval(statusIntervalRef.current);
      }
      if (unsubscribeRef.current !== null) {
        unsubscribeRef.current();
      }
      if (heliaNodeRef.current !== null && heliaServiceRef.current !== null) {
        void heliaServiceRef.current.stopNode(heliaNodeRef.current);
      }
      if (nodeRef.current !== null && serviceRef.current !== null) {
        void serviceRef.current.stopNode(nodeRef.current);
      }
    }, []);

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Minimal PubSub + Helia Test v1.31</h1>

      {/* Connection Controls */}
      <div style={{ marginBottom: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '8px' }}>
        <h3>Connection</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => { void handleConnect(); }}
            disabled={isConnected || isConnecting}
            style={{
              padding: '10px 20px',
              background: isConnected ? '#ccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isConnected || isConnecting ? 'not-allowed' : 'pointer'
            }}
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
          </button>
          <button
            onClick={() => { void handleDisconnect(); }}
            disabled={!isConnected}
            style={{
              padding: '10px 20px',
              background: !isConnected ? '#ccc' : '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: !isConnected ? 'not-allowed' : 'pointer'
            }}
          >
            Disconnect
          </button>
          <span style={{
            padding: '5px 10px',
            background: isConnected ? '#4CAF50' : '#f44336',
            color: 'white',
            borderRadius: '4px'
          }}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        {peerId !== '' && (
          <div style={{ marginTop: '10px', fontSize: '12px', wordBreak: 'break-all' }}>
            <strong>Peer ID:</strong> {peerId}
          </div>
        )}
      </div>

      {/* Status */}
      <div style={{ marginBottom: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '8px' }}>
        <h3>PubSub Status</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{connectionCount}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Connections</div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{subscriberCount}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Subscribers</div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>{sentCount}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>PubSub Sent</div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196F3' }}>{receivedCount}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>PubSub Recv</div>
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div style={{ marginBottom: '20px', padding: '15px', background: '#e8f5e9', borderRadius: '8px' }}>
        <h3>File Upload (Helia)</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => { void handleFileUpload(e); }}
            disabled={!isConnected || isUploading}
            style={{ flex: 1 }}
          />
          {isUploading && <span style={{ color: '#666' }}>Uploading to browser memory...</span>}
        </div>
        {uploadedFiles.length > 0 && (
          <div style={{ marginTop: '10px', fontSize: '12px' }}>
            <strong>Uploaded Files:</strong>
            {uploadedFiles.map((f, i) => (
              <div key={i} style={{
                marginTop: '5px',
                padding: '10px',
                background: f.confirmed ? '#a5d6a7' : '#c8e6c9',
                borderRadius: '4px',
                border: f.confirmed ? '2px solid #4CAF50' : 'none'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div><strong>{f.name}</strong> ({f.size} bytes)</div>
                  {f.confirmed ? (
                    <span style={{
                      background: '#4CAF50',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      HAP Cloned File
                    </span>
                  ) : (
                    <span style={{
                      background: '#ff9800',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px'
                    }}>
                      Message Sent (but not necessarily received) to HAP...
                    </span>
                  )}
                </div>
                <div style={{ wordBreak: 'break-all', color: '#666', marginTop: '4px' }}>CID: {f.cid}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cloned Files (from other peers) */}
      {clonedFiles.length > 0 && (
        <div style={{ marginBottom: '20px', padding: '15px', background: '#e1f5fe', borderRadius: '8px' }}>
          <h3>Available Files (from network)</h3>
          <div style={{ fontSize: '12px' }}>
            {clonedFiles.map((f, i) => (
              <div key={i} style={{
                marginTop: '5px',
                padding: '10px',
                background: f.status === 'ready' ? '#b3e5fc' : '#e0e0e0',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div><strong>{f.name}</strong> ({f.size} bytes)</div>
                  <div style={{ wordBreak: 'break-all', color: '#666', fontSize: '10px' }}>
                    CID: {f.cid.substring(0, 30)}...
                  </div>
                </div>
                <button
                  onClick={() => { void handleDownload(f.cid, f.name); }}
                  disabled={f.status !== 'ready'}
                  style={{
                    padding: '8px 16px',
                    background: f.status === 'ready' ? '#2196F3' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: f.status === 'ready' ? 'pointer' : 'not-allowed'
                  }}
                >
                  {f.status === 'cloning' ? 'Cloning...' : 'Download'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Send Message */}
      <div style={{ marginBottom: '20px', padding: '15px', background: '#fff3e0', borderRadius: '8px' }}>
        <h3>Send Message</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={messageInput}
            onChange={(e) => { setMessageInput(e.target.value); }}
            placeholder="Enter message..."
            style={{
              flex: 1,
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontFamily: 'monospace'
            }}
          />
          <button
            onClick={() => { void handleSendMessage(); }}
            disabled={!isConnected}
            style={{
              padding: '10px 20px',
              background: !isConnected ? '#ccc' : '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: !isConnected ? 'not-allowed' : 'pointer'
            }}
          >
            Send
          </button>
        </div>
      </div>

      {/* Log */}
      <div style={{ padding: '15px', background: '#263238', borderRadius: '8px', color: '#fff' }}>
        <h3 style={{ color: '#fff', marginTop: 0 }}>Log</h3>
        <div style={{
          height: '300px',
          overflow: 'auto',
          fontSize: '12px',
          lineHeight: '1.5'
        }}>
          {logs.length === 0 ? (
            <div style={{ color: '#888' }}>No logs yet. Click Connect to start.</div>
          ) : (
            logs.map((log, i) => (
              <div key={i} style={{
                color: log.type === 'error' ? '#f44336' :
                       log.type === 'sent' ? '#4CAF50' :
                       log.type === 'received' ? '#2196F3' :
                       log.type === 'file' ? '#9C27B0' : '#fff'
              }}>
                [{log.timestamp.toLocaleTimeString()}] {log.message}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
